<script setup lang="ts">
/**
 * In-app alert when the AI hands a chat to ME (socket "ai.handoff", docs/ai-mode.md): top right on every page, stays
 * until opened or closed, with a soft chime (stores/aimode.ts ring → lib/chime.ts). The speaker button switches the
 * chime off/on for this person on this browser (same setting as the account menu "เสียงเตือน").
 * The chat itself also sits in "ต้องตอบ" with a warning chip, so nothing is lost if this is missed.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { HANDOFF_LABEL } from '../../api/aimode/types'
import { useAiMode } from '../../stores/aimode'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const aimode = useAiMode()
const inbox = useInbox()
const router = useRouter()

const items = computed(() =>
  aimode.alerts.map((a) => ({
    ...a,
    name: inbox.convs[a.conversationId]?.contact.name ?? 'ลูกค้า',
    label: HANDOFF_LABEL[a.code] ?? 'AI ส่งต่อให้พนักงาน',
    note: a.note && a.note !== (HANDOFF_LABEL[a.code] ?? '') ? a.note : '',
  })),
)

function open(key: string, convId: string): void {
  aimode.dismiss(key)
  void router.push(`/c/${convId}`)
}
</script>

<template>
  <div v-if="items.length" class="aha" role="region" aria-label="AI ส่งต่อแชทให้คุณ" aria-live="polite">
    <div v-for="a in items" :key="a.key" class="aha-i">
      <span class="aha-ic"><Icon name="handoff" /></span>
      <div class="aha-b">
        <b>AI ส่งต่อแชทให้คุณ</b>
        <span
          ><span class="aha-who">{{ a.name }}</span> · {{ a.label }}<template v-if="a.note"> · {{ a.note }}</template></span
        >
      </div>
      <button
        type="button"
        class="aha-snd"
        :class="{ off: !aimode.soundOn }"
        role="switch"
        :aria-checked="aimode.soundOn"
        :aria-label="aimode.soundOn ? 'ปิดเสียงเตือน' : 'เปิดเสียงเตือน'"
        :title="aimode.soundOn ? 'เสียงเตือนเปิดอยู่ · กดเพื่อปิด' : 'เสียงเตือนปิดอยู่ · กดเพื่อเปิด'"
        @click="aimode.setSound(!aimode.soundOn)"
      >
        <Icon :name="aimode.soundOn ? 'volume' : 'volumeOff'" />
      </button>
      <button type="button" class="aha-open" @click="open(a.key, a.conversationId)">เปิดแชท</button>
      <button type="button" class="aha-x" aria-label="ปิดการแจ้งเตือน" @click="aimode.dismiss(a.key)"><Icon name="x" /></button>
    </div>
  </div>
</template>

<style scoped>
.aha {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 380px;
}
.aha-i {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #f0dca6;
  box-shadow: 0 10px 28px rgba(23, 33, 28, 0.16);
}
.aha-ic {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #fde9b8;
  color: #8a5100;
}
.aha-ic :deep(.ic) {
  width: 17px;
  height: 17px;
}
.aha-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 12.5px;
  line-height: 19px;
  color: #6b4300;
}
.aha-b b {
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ink);
}
.aha-b > span {
  overflow-wrap: anywhere;
}
.aha-who {
  font-weight: 600;
  color: #4a3200;
}
.aha-open {
  flex: none;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--ac);
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.aha-open:hover {
  background: var(--ac2);
}
.aha-snd {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  color: var(--ac2);
}
.aha-snd:hover {
  background: var(--acs);
}
.aha-snd.off {
  color: #8a9a92;
}
.aha-snd :deep(.ic) {
  width: 17px;
  height: 17px;
}
.aha-x {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  color: var(--mut);
}
.aha-x:hover {
  background: #f4f6f5;
}
.aha-x :deep(.ic) {
  width: 15px;
  height: 15px;
}
</style>
